import { hasOwn } from "./hasOwn";

// Formatting shared by the AI usage pages. Costs arrive from
// convex/observability in micro-USD (millionths of a dollar).

export const TOOL_STATUS_LABEL: Record<string, string> = {
  sanctioned: "sanctioned",
  review: "review",
  not_allowed: "not allowed",
};

export function formatUsd(micro: number | null | undefined): string {
  if (typeof micro !== "number" || !Number.isFinite(micro)) return "not priced";
  const usd = micro / 1_000_000;
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return "<$0.01";
  if (usd < 1000) return `$${usd.toFixed(2)}`;
  return `$${Math.round(usd).toLocaleString("en-US")}`;
}

// A spend in two parts: the amount, and the calls left out of it. No calls
// means Steerly saw none (an unrouted tool's spend is not known); calls none of
// which Convex could price are "not priced". Neither is ever "$0.00".
export function spendParts(
  costMicroUsd: number,
  calls: number,
  unpricedCalls: number
): { amount: string; unpriced: string | null; priced: boolean } {
  if (calls <= 0) return { amount: "no calls seen", unpriced: null, priced: false };
  if (unpricedCalls >= calls) return { amount: "not priced", unpriced: null, priced: false };
  const left = unpricedCalls === 1 ? "1 call" : `${formatCount(unpricedCalls)} calls`;
  return {
    amount: formatUsd(costMicroUsd),
    unpriced: unpricedCalls > 0 ? `${left} not priced` : null,
    // The amount is a dollar figure, at API list prices.
    priced: true,
  };
}

export function formatSpend(costMicroUsd: number, calls: number, unpricedCalls: number): string {
  const { amount, unpriced } = spendParts(costMicroUsd, calls, unpricedCalls);
  return unpriced ? `${amount} + ${unpriced}` : amount;
}

export function formatCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return String(Math.round(n));
  // Below 999,500, so a count that rounds to 1000k reads as 1M instead.
  if (n < 999_500) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, "")}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
}

export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return "0%";
  if (fraction < 0.01) return "<1%";
  return `${Math.round(fraction * 100)}%`;
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function formatWhen(ms: number, now: number = Date.now()): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  // hourCycle, not hour12: false, which some engines render as "24:05" at
  // five past midnight.
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  if (new Date(now).toDateString() === d.toDateString()) return time;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

// Why a Steerly session did not go through the gateway. The host names these
// (src/gateway/injection.js, src/terminalEngine.js, src/observability/records.js);
// Convex counts a reason it cannot use as "unknown" and folds the reasons past
// its top 10 into "~other" (convex/observability/lib.ts).
const REASONS: Record<string, string> = {
  "custom-provider": "uses its own model provider",
  disabled: "gateway was off when it started",
  "gateway-unhealthy": "gateway was unhealthy",
  "no-org": "no signed-in org",
  "tool-not-supported": "tool not supported by the gateway yet",
  "stale-route": "its gateway route did not survive a host restart",
  "token-lost": "its gateway token was lost in a host restart",
  unavailable: "gateway unavailable",
  // The host's reason when it has no particular one (src/observability/records.js).
  "not-routed": "no reason given",
  unknown: "reason not recorded",
  "~other": "other reasons",
};

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return "no reason given";
  // Own keys only: "constructor" is a valid reason, and a plain lookup would
  // return Object.prototype's member for it.
  return hasOwn(REASONS, reason) ? REASONS[reason] : reason;
}

// `lead` (such as "not routed") with why: the reason when there is one, and
// nothing more when the host gave none ("not-routed", or no reason at all),
// so "not routed" is never said twice. A reason Convex has no record of
// ("unknown") is said to be not recorded.
export function notRoutedWhy(lead: string, reason: string | null | undefined): string {
  if (!reason || reason === "not-routed") return lead;
  if (reason === "unknown") return `${lead} (reason not recorded)`;
  return `${lead}: ${reasonLabel(reason)}`;
}

const DAY_MS = 86_400_000;

// The start of a period of `days` whole UTC days ending today: UTC midnight of
// today minus (days - 1). Convex counts usage by whole UTC days from the day of
// `since`, so the overview's sessions start at the same midnight as its usage,
// and the overview and AI spend cover the same days. The value holds all day,
// so a subscription keeps the same arguments (queries never read the clock).
export function periodStart(days: number, now: number = Date.now()): number {
  const today = Math.floor(now / DAY_MS) * DAY_MS;
  return today - (days - 1) * DAY_MS;
}

// The inclusive UTC day range of an AI spend period: the last 7 or 30 days,
// today included, or the month so far. finops.summary counts spend by whole
// UTC days, as the overview does (periodStart). Day strings change once a
// day, so the subscription keeps its arguments (queries never read the clock).
export function dayRange(
  period: "7" | "30" | "mtd",
  now: number = Date.now()
): { fromDay: string; toDay: string } {
  const toDay = new Date(now).toISOString().slice(0, 10);
  if (period === "mtd") return { fromDay: `${toDay.slice(0, 8)}01`, toDay };
  const fromDay = new Date(periodStart(Number(period), now)).toISOString().slice(0, 10);
  return { fromDay, toDay };
}

export type SessionStatus = "live" | "idle" | "ended" | "stopped";
// How long after it was last seen a session still reads "live".
export const LIVE_MS = 5 * 60_000;

export function sessionStatus(
  s: { lastSeenAt: number; endedAt: number | null; stoppedAt: number | null },
  now: number = Date.now()
): SessionStatus {
  if (s.stoppedAt) return "stopped";
  if (s.endedAt) return "ended";
  return now - s.lastSeenAt < LIVE_MS ? "live" : "idle";
}

export function projectLabel(s: {
  repo: string | null;
  cwd: string | null;
  gitBranch?: string | null;
}): string {
  const base = s.repo ?? (s.cwd ? (s.cwd.split(/[\\/]/).filter(Boolean).pop() ?? "") : "");
  if (!base) return "";
  return s.gitBranch ? `${base} (${s.gitBranch})` : base;
}

export function sourceLabel(sourceKind: string): string {
  if (sourceKind === "steerly-terminal") return "Steerly terminal";
  if (sourceKind === "steerly-chat") return "Steerly chat";
  return "Outside Steerly";
}
